import { useCallback } from 'react';
import { type Editor } from '@tiptap/react';
import { Button } from '@mantine/core';
import { RiUnderline } from 'react-icons/ri';
import { ToolbarContainer } from './styles';
import { theme } from '../globalStyle/global-styles';
import { FiLink } from 'react-icons/fi';

const Toolbar = ({ editor }: { editor: Editor | null }) => {
  // Detect if we're on a mobile device
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  const setLink = useCallback(() => {
    const previousUrl = editor?.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);

    // cancelled
    if (url === null) {
      return;
    }

    // empty
    if (url === '') {
      if (isMobile) {
        editor?.chain().extendMarkRange('link').unsetLink().run();
      } else {
        editor?.chain().focus().extendMarkRange('link').unsetLink().run();
      }
      return;
    }

    // update link
    if (isMobile) {
      editor
        ?.chain()
        .extendMarkRange('link')
        .setLink({ href: url })
        .run();
    } else {
      editor
        ?.chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: url })
        .run();
    }
  }, [editor, isMobile]);
  if (!editor) {
    return null;
  }

  return (
    <ToolbarContainer className='toolbar'>
      <Button
        onClick={setLink}
        className={editor.isActive('link') ? 'is-active' : ''}
      >
        <FiLink />
      </Button>
      <Button
        onClick={() => {
          if (isMobile) {
            editor.chain().toggleUnderline().run();
          } else {
            editor.chain().focus().toggleUnderline().run();
          }
        }}
        disabled={!editor.can().chain().focus().toggleUnderline().run()}
        className={editor.isActive('underline') ? 'is-active' : ''}
      >
        <RiUnderline />
      </Button>
      {/* <Button
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        className={editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}
      >
        <RiH3 />
      </Button> */}
      <Button
        onClick={() => {
          if (isMobile) {
            editor.chain().toggleBold().run();
          } else {
            editor.chain().focus().toggleBold().run();
          }
        }}
        disabled={!editor.can().chain().focus().toggleBold().run()}
        className={editor.isActive('bold') ? 'is-active' : ''}
      >
        <span style={{ fontFamily: theme.fonts.Medium, fontWeight: 'bold' }}>
          B
        </span>
      </Button>

      <Button className="separator">|</Button>
      {/* <Button
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={!editor.can().chain().focus().toggleItalic().run()}
        className={editor.isActive('italic') ? 'is-active' : ''}
      >
        <RiItalic />
      </Button>
      <Button
        onClick={() => editor.chain().focus().toggleStrike().run()}
        disabled={!editor.can().chain().focus().toggleStrike().run()}
        className={editor.isActive('strike') ? 'is-active' : ''}
      >
        <RiStrikethrough />
      </Button>
      <Button
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={editor.isActive('bulletList') ? 'is-active' : ''}
      >
        <FaListUl />
      </Button>
      <Button
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={editor.isActive('orderedList') ? 'is-active' : ''}
      >
        <FaListOl />
      </Button> */}
      <Button
        onClick={() => {
          if (isMobile) {
            editor.chain().setTextAlign('right').run();
          } else {
            editor.chain().focus().setTextAlign('right').run();
          }
        }}
        className={editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''}
      >
        <svg
          width="13"
          height="8"
          viewBox="0 0 13 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M11.75 0.9375H1.25C1.0085 0.9375 0.8125 0.7415 0.8125 0.5C0.8125 0.2585 1.0085 0.0625 1.25 0.0625H11.75C11.9915 0.0625 12.1875 0.2585 12.1875 0.5C12.1875 0.7415 11.9915 0.9375 11.75 0.9375ZM12.1875 4C12.1875 3.7585 11.9915 3.5625 11.75 3.5625H1.25C1.0085 3.5625 0.8125 3.7585 0.8125 4C0.8125 4.2415 1.0085 4.4375 1.25 4.4375H11.75C11.9915 4.4375 12.1875 4.2415 12.1875 4ZM12.1875 7.5C12.1875 7.2585 11.9915 7.0625 11.75 7.0625H6.5C6.2585 7.0625 6.0625 7.2585 6.0625 7.5C6.0625 7.7415 6.2585 7.9375 6.5 7.9375H11.75C11.9915 7.9375 12.1875 7.7415 12.1875 7.5Z"
            fill="#25314C"
          />
        </svg>
      </Button>
      <Button
        onClick={() => {
          if (isMobile) {
            editor.chain().setTextAlign('center').run();
          } else {
            editor.chain().focus().setTextAlign('center').run();
          }
        }}
        className={editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''}
      >
        <svg
          width="13"
          height="8"
          viewBox="0 0 13 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0.8125 0.5C0.8125 0.2585 1.0085 0.0625 1.25 0.0625H11.75C11.9915 0.0625 12.1875 0.2585 12.1875 0.5C12.1875 0.7415 11.9915 0.9375 11.75 0.9375H1.25C1.0085 0.9375 0.8125 0.7415 0.8125 0.5ZM11.75 3.5625H1.25C1.0085 3.5625 0.8125 3.7585 0.8125 4C0.8125 4.2415 1.0085 4.4375 1.25 4.4375H11.75C11.9915 4.4375 12.1875 4.2415 12.1875 4C12.1875 3.7585 11.9915 3.5625 11.75 3.5625ZM11.75 7.0625H1.25C1.0085 7.0625 0.8125 7.2585 0.8125 7.5C0.8125 7.7415 1.0085 7.9375 1.25 7.9375H11.75C11.9915 7.9375 12.1875 7.7415 12.1875 7.5C12.1875 7.2585 11.9915 7.0625 11.75 7.0625Z"
            fill="#25314C"
          />
        </svg>
      </Button>
      <Button
        onClick={() => {
          if (isMobile) {
            editor.chain().setTextAlign('left').run();
          } else {
            editor.chain().focus().setTextAlign('left').run();
          }
        }}
        className={editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''}
      >
        <svg
          width="13"
          height="8"
          viewBox="0 0 13 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M8.83333 0.9375H1.25C1.0085 0.9375 0.8125 0.7415 0.8125 0.5C0.8125 0.2585 1.0085 0.0625 1.25 0.0625H8.83333C9.07483 0.0625 9.27083 0.2585 9.27083 0.5C9.27083 0.7415 9.07483 0.9375 8.83333 0.9375ZM12.1875 4C12.1875 3.7585 11.9915 3.5625 11.75 3.5625H1.25C1.0085 3.5625 0.8125 3.7585 0.8125 4C0.8125 4.2415 1.0085 4.4375 1.25 4.4375H11.75C11.9915 4.4375 12.1875 4.2415 12.1875 4ZM6.9375 7.5C6.9375 7.2585 6.7415 7.0625 6.5 7.0625H1.25C1.0085 7.0625 0.8125 7.2585 0.8125 7.5C0.8125 7.7415 1.0085 7.9375 1.25 7.9375H6.5C6.7415 7.9375 6.9375 7.7415 6.9375 7.5Z"
            fill="#25314C"
          />
        </svg>
      </Button>
    </ToolbarContainer>
  );
};

export default Toolbar;
